'use client'

import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { LaborMetricsModel } from '@/types/clarity'

import {
  DataTableColumnCell,
  DataTableColumnHeader,
  Format,
} from '@/components/ui/data-table'

const columnHelper = createColumnHelper<LaborMetricsModel>()

const groupedColumns = [
  columnHelper.group({
    id: 'managerGroup',
    header: () => 'Manager',
    enableHiding: true,
    columns: [
      {
        accessorKey: 'managerLaborRatio',
        header: ({ column }) => {
          return DataTableColumnHeader({
            column,
            title: 'Labor Ratio',
          })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'managerLaborRatio',
            format: Format.PERCENT,
          })
        },
        enableSorting: true,
      },
      {
        accessorKey: 'managerTipHours',
        header: ({ column }) => {
          return DataTableColumnHeader({ column, title: 'Tip Hours' })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'managerTipHours',
            format: Format.NUMBER,
          })
        },
      },
      {
        accessorKey: 'managerNoTipHours',
        header: ({ column }) => {
          return DataTableColumnHeader({ column, title: 'No Tip Hours' })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'managerNoTipHours',
            format: Format.NUMBER,
          })
        },
      },
    ],
  }),
  columnHelper.group({
    id: 'baristaGroup',
    header: () => 'Barista',
    enableHiding: true,
    columns: [
      {
        accessorKey: 'baristaLaborRatio',
        header: ({ column }) => {
          return DataTableColumnHeader({ column, title: 'Labor Ratio' })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'baristaLaborRatio',
            format: Format.PERCENT,
          })
        },
        enableSorting: true,
      },
      {
        accessorKey: 'baristaTipHours',
        header: ({ column }) => {
          return DataTableColumnHeader({ column, title: 'Tip Hours' })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'baristaTipHours',
            format: Format.NUMBER,
          })
        },
        enableSorting: true,
      },
      {
        accessorKey: 'baristaNoTipHours',
        header: ({ column }) => {
          return DataTableColumnHeader({ column, title: 'No Tip Hours' })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'baristaNoTipHours',
            format: Format.NUMBER,
          })
        },
      },
    ],
  }),
  columnHelper.group({
    id: 'trainingGroup',
    header: () => 'Training',
    enableHiding: true,
    columns: [
      {
        accessorKey: 'trainingLaborRatio',
        header: ({ column }) => {
          return DataTableColumnHeader({ column, title: 'Labor Ratio' })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'trainingLaborRatio',
            format: Format.PERCENT,
          })
        },
        enableSorting: true,
      },
      {
        accessorKey: 'trainingLaborHours',
        header: ({ column }) => {
          return DataTableColumnHeader({ column, title: 'Labor Hours' })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'trainingLaborHours',
            format: Format.NUMBER,
          })
        },
        enableSorting: true,
      },
    ],
  }),
  columnHelper.group({
    id: 'marketingGroup',
    header: () => 'Marketing',
    enableHiding: true,
    columns: [
      {
        accessorKey: 'marketingLaborRatio',
        header: ({ column }) => {
          return DataTableColumnHeader({ column, title: 'Labor Ratio' })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'marketingLaborRatio',
            format: Format.PERCENT,
          })
        },
        enableSorting: true,
      },
      {
        accessorKey: 'marketingLaborHours',
        header: ({ column }) => {
          return DataTableColumnHeader({ column, title: 'Labor Hours' })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'marketingLaborHours',
            format: Format.NUMBER,
          })
        },
        enableSorting: true,
      },
    ],
  }),
  columnHelper.group({
    id: 'meetingGroup',
    header: () => 'Meeting',
    enableHiding: true,
    columns: [
      {
        accessorKey: 'meetingLaborRatio',
        header: ({ column }) => {
          return DataTableColumnHeader({ column, title: 'Labor Ratio' })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'meetingLaborRatio',
            format: Format.PERCENT,
          })
        },
        enableSorting: true,
      },
      {
        accessorKey: 'meetingLaborHours',
        header: ({ column }) => {
          return DataTableColumnHeader({ column, title: 'Labor Hours' })
        },
        cell: ({ row }) => {
          return DataTableColumnCell({
            row,
            accessorKey: 'meetingLaborHours',
            format: Format.NUMBER,
          })
        },
        enableSorting: true,
      },
    ],
  }),
]

export const groupedLaborMetricsColumns: ColumnDef<LaborMetricsModel>[] = [
  {
    accessorKey: 'stand',
    header: 'Stand',
    cell: ({ row }) => {
      return DataTableColumnCell({
        row,
        accessorKey: 'stand',
      })
    },
    enableHiding: false,
  },
  {
    accessorKey: 'laborRatio',
    header: ({ column }) => {
      return DataTableColumnHeader({ column, title: 'Labor Ratio' })
    },
    cell: ({ row }) => {
      return DataTableColumnCell({
        row,
        accessorKey: 'laborRatio',
        format: Format.PERCENT,
      })
    },
    enableSorting: true,
  },
  {
    accessorKey: 'laborHours',
    header: ({ column }) => {
      return DataTableColumnHeader({ column, title: 'Labor Hours' })
    },
    cell: ({ row }) => {
      return DataTableColumnCell({
        row,
        accessorKey: 'laborHours',
        format: Format.NUMBER,
      })
    },
    enableSorting: true,
  },
  ...groupedColumns,
  {
    accessorKey: 'tcCount',
    header: ({ column }) => {
      return DataTableColumnHeader({ column, title: 'TC Count' })
    },
    cell: ({ row }) => {
      return DataTableColumnCell({
        row,
        accessorKey: 'tcCount',
        format: Format.NUMBER,
      })
    },
    enableSorting: true,
  },
]
